import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CalendarClock, Plus, Trash2, Video } from 'lucide-react';
import { interviewsAPI } from '@/utils/api';
import { toCvInput, fromCvInput, browserTz } from '@/utils/prospectTime';
import { CvDateTimeInput } from '@/components/CvDateTimeInput';

// Horários sempre em hora de Cabo Verde, independentemente do fuso do browser.
const defaultSlot = (daysAhead, hour) => `${toCvInput(new Date(Date.now() + daysAhead * 86400000)).slice(0, 10)}T${String(hour).padStart(2, '0')}:00`;

export const ProposeInterviewModal = ({ app, open, onClose, onProposed, onAcceptOnly }) => {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([defaultSlot(2, 10), defaultSlot(3, 15)]);
  const [duration, setDuration] = useState(30);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const candName = app?.candidate?.full_name || app?.candidate_name || '';
  const setSlot = (i, v) => setSlots((s) => s.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    setError('');
    const iso = slots.filter(Boolean).map(fromCvInput);
    if (!iso.length) return setError(t('interviews.propose.errNoSlots'));
    setBusy(true);
    try {
      const { data } = await interviewsAPI.propose({ application_id: app.id, slots: iso, duration_min: Number(duration), note: note || null, scheduler_tz: browserTz() });
      onProposed?.(data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || t('interviews.propose.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="propose-interview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><CalendarClock className="h-5 w-5 text-emerald-600" />{t('interviews.propose.title')}</DialogTitle>
          <DialogDescription>{t('interviews.propose.subtitle', { name: candName, job: app?.job_title || '' })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-gray-800">{t('interviews.propose.slotsLabel')}</label>
            <p className="text-xs text-gray-500 mb-2">{t('interviews.propose.slotsHint')}</p>
            <div className="space-y-2">
              {slots.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CvDateTimeInput value={s} onChange={(v) => setSlot(i, v)} testid={`slot-input-${i}`} />
                  {slots.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => setSlots(slots.filter((_, idx) => idx !== i))} data-testid={`slot-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {slots.length < 5 && (
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setSlots([...slots, defaultSlot(slots.length + 2, 10)])} data-testid="slot-add">
                <Plus className="h-4 w-4 mr-1" />{t('interviews.propose.addSlot')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-gray-800">{t('interviews.propose.duration')}</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="duration-select">
                {[15, 30, 45, 60].map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 flex items-start gap-2">
              <Video className="h-4 w-4 mt-0.5 flex-shrink-0" />{t('interviews.propose.videoHint')}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-800">{t('interviews.propose.note')}</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t('interviews.propose.notePlaceholder')} data-testid="note-input" className="mt-1" />
          </div>

          {error && <p className="text-sm text-red-600" data-testid="propose-error">{error}</p>}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
            <button type="button" onClick={() => { onAcceptOnly?.(); onClose(); }} className="text-xs text-gray-500 hover:text-gray-800 underline" data-testid="accept-only-btn">
              {t('interviews.propose.acceptOnly')}
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="propose-cancel">{t('interviews.propose.cancel')}</Button>
              <Button type="button" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="propose-submit">
                {busy ? t('interviews.propose.sending') : t('interviews.propose.submit')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
